import React from 'react';
import { useLanguage } from '../lib/LanguageContext';
import ResponsibleUseFr from './fr/ResponsibleUseFr';
import '../styles/Document.css';

function ResponsibleUse() {
  const { isFr } = useLanguage();
  if (isFr) return <ResponsibleUseFr />;

  return (
    <div className="doc-page">
      <div className="doc-header">
        <span className="doc-label">Collections</span>
        <h1 className="doc-title">
          MegaTransact Responsible Use and Collections Notice
        </h1>
        <p className="doc-intro">
          How MegaTransact approaches responsible use, repayment follow-up, and collections
          activity. MegaTransact is a digital platform beginning with salary-based lending and
          expanding progressively into practical money services.
        </p>
        <div className="doc-meta">
          <span className="doc-meta-item">
            <strong>Effective Date:</strong> March 27, 2026
          </span>
          <span className="doc-meta-item">
            <strong>Last Updated:</strong> March 27, 2026
          </span>
        </div>
      </div>

      <div className="doc-body">
        <div className="doc-preamble">
          <p>
            This Responsible Use and Collections Notice applies to users of MegaTransact
            services, especially users of MegaTransact's salary-based lending service. It should
            be read together with the MegaTransact Terms and Conditions, the MegaTransact Privacy
            Policy, and the MegaTransact Salary-Based Lending Terms.
          </p>
        </div>

        {/* Section 1 */}
        <div className="doc-section">
          <span className="section-number">Section 1</span>
          <h2 className="section-title">Purpose of This Notice</h2>
          <p>
            MegaTransact aims to provide structured and responsible access to salary-linked
            financial support. This Notice explains:
          </p>
          <ul>
            <li>the standards users are expected to follow</li>
            <li>how MegaTransact encourages responsible use</li>
            <li>what happens when repayment is missed or delayed</li>
            <li>
              how MegaTransact may communicate with users and workplace-linked channels where
              lawful and appropriate
            </li>
            <li>
              the general approach MegaTransact takes to reminders, restrictions, and recovery
              activity
            </li>
          </ul>
        </div>

        {/* Section 2 */}
        <div className="doc-section">
          <span className="section-number">Section 2</span>
          <h2 className="section-title">Responsible Use of MegaTransact</h2>
          <p>
            Users are expected to use MegaTransact responsibly, honestly, and in line with the
            platform's intended purpose.
          </p>
          <p>Responsible use includes:</p>
          <ul>
            <li>providing accurate and complete information</li>
            <li>
              applying only for support the user reasonably expects to manage
            </li>
            <li>reviewing repayment terms carefully before acceptance</li>
            <li>keeping account and contact details current</li>
            <li>
              responding to verification and support requests in a timely manner
            </li>
            <li>
              avoiding misuse, deception, duplicate accounts, or manipulated applications
            </li>
            <li>
              engaging with MegaTransact promptly if circumstances change
            </li>
          </ul>
          <p>
            MegaTransact may take responsible-use history into account when assessing future
            access to services.
          </p>
        </div>

        {/* Section 3 */}
        <div className="doc-section">
          <span className="section-number">Section 3</span>
          <h2 className="section-title">
            MegaTransact's Approach to Responsible Service Administration
          </h2>
          <p>
            MegaTransact may apply internal safeguards designed to support responsible service
            use and reduce avoidable repayment pressure.
          </p>
          <p>These safeguards may include:</p>
          <ul>
            <li>eligibility checks</li>
            <li>identity and employment verification</li>
            <li>salary-linked assessment</li>
            <li>internal repayment cap rules</li>
            <li>application review and approval controls</li>
            <li>service limits based on prior platform conduct</li>
            <li>
              temporary or permanent restrictions where misuse or risk concerns arise
            </li>
          </ul>
          <p>
            These measures are intended to support careful and sustainable service
            administration.
          </p>
        </div>

        {/* Section 4 */}
        <div className="doc-section">
          <span className="section-number">Section 4</span>
          <h2 className="section-title">Repayment Expectations</h2>
          <p>
            Where a user accepts salary-based support through MegaTransact, the user is expected
            to meet the repayment terms that were presented and accepted.
          </p>
          <p>Users should:</p>
          <ul>
            <li>understand the total repayment amount before acceptance</li>
            <li>
              understand the repayment dates or salary-linked repayment structure
            </li>
            <li>
              ensure they remain reachable through the contact details provided
            </li>
            <li>
              notify MegaTransact where relevant employment or repayment circumstances
              materially change
            </li>
          </ul>
          <p>
            Repayment obligations remain in effect unless MegaTransact formally confirms a
            change, waiver, restructuring, or closure.
          </p>
        </div>

        {/* Section 5 */}
        <div className="doc-section">
          <span className="section-number">Section 5</span>
          <h2 className="section-title">When a Repayment Is Missed or Delayed</h2>
          <p>
            A repayment may be treated as missed, delayed, or failed where:
          </p>
          <ul>
            <li>payment is not received on time</li>
            <li>
              a payroll-linked deduction does not occur as expected
            </li>
            <li>
              a disbursement-related obligation remains unpaid
            </li>
            <li>
              a payment is reversed, rejected, blocked, or otherwise unsuccessful
            </li>
            <li>
              a user avoids or ignores reasonable repayment follow-up
            </li>
          </ul>
          <p>
            A missed or delayed repayment does not automatically remove the obligation. The
            outstanding amount may remain due unless otherwise agreed in writing or through an
            approved MegaTransact process.
          </p>
        </div>

        {/* Section 6 */}
        <div className="doc-section">
          <span className="section-number">Section 6</span>
          <h2 className="section-title">Reminder and Follow-Up Process</h2>
          <p>
            If a repayment is missed or delayed, MegaTransact may take one or more reasonable
            follow-up steps, including:
          </p>
          <ul>
            <li>sending reminders through the app</li>
            <li>
              sending SMS, email, WhatsApp, or other approved communications
            </li>
            <li>calling the user where appropriate</li>
            <li>
              notifying the user of outstanding status and next steps
            </li>
            <li>requesting clarification or updated information</li>
            <li>
              applying temporary restrictions to account access or future requests
            </li>
          </ul>
          <p>
            MegaTransact aims to use a measured and professional tone in follow-up
            communications.
          </p>
        </div>

        {/* Section 7 */}
        <div className="doc-section">
          <span className="section-number">Section 7</span>
          <h2 className="section-title">Workplace-Linked Follow-Up</h2>
          <p>
            Because MegaTransact begins with salary-based lending, some repayment arrangements
            may be linked to an employer or workplace process.
          </p>
          <p>
            Where lawful, contractually supported, and operationally relevant, MegaTransact may
            engage with employer-linked channels for purposes such as:
          </p>
          <ul>
            <li>confirming payroll timing</li>
            <li>confirming employment status</li>
            <li>
              following up on repayment processes linked to salary administration
            </li>
            <li>
              resolving administrative issues affecting deductions or repayments
            </li>
          </ul>
          <p>
            MegaTransact will aim to limit such contact to what is reasonably necessary for
            service administration, repayment management, fraud prevention, or lawful recovery.
          </p>
        </div>

        {/* Section 8 */}
        <div className="doc-section">
          <span className="section-number">Section 8</span>
          <h2 className="section-title">Service Restrictions After Non-Payment</h2>
          <p>
            If repayment remains unpaid, delayed, or unresolved, MegaTransact may restrict access
            to some or all platform features, including:
          </p>
          <ul>
            <li>blocking new applications</li>
            <li>suspending salary-based lending access</li>
            <li>limiting account functionality</li>
            <li>placing the account under manual review</li>
            <li>
              requiring additional verification before future service use
            </li>
          </ul>
          <p>
            Restrictions may remain in place until the issue is resolved to MegaTransact's
            satisfaction.
          </p>
        </div>

        {/* Section 9 */}
        <div className="doc-section">
          <span className="section-number">Section 9</span>
          <h2 className="section-title">Collections Activity</h2>
          <p>
            Where an amount remains outstanding, MegaTransact may take lawful and proportionate
            recovery steps.
          </p>
          <p>These may include:</p>
          <ul>
            <li>repeated reminders and notices</li>
            <li>account restriction or suspension</li>
            <li>structured repayment follow-up</li>
            <li>
              employer-linked administrative follow-up where lawful and applicable
            </li>
            <li>referral to internal or external recovery support</li>
            <li>
              use of lawful enforcement or dispute-resolution channels where necessary
            </li>
          </ul>
          <p>
            MegaTransact does not commit to taking every step in every case and may determine
            the appropriate response based on the circumstances.
          </p>
        </div>

        {/* Section 10 */}
        <div className="doc-section">
          <span className="section-number">Section 10</span>
          <h2 className="section-title">User Cooperation</h2>
          <p>
            Users are expected to cooperate in good faith where repayment issues arise.
          </p>
          <p>This includes:</p>
          <ul>
            <li>responding to communications</li>
            <li>confirming relevant facts honestly</li>
            <li>avoiding false statements or evasive conduct</li>
            <li>following approved repayment or resolution processes</li>
            <li>
              not interfering with platform controls, reminders, or verification checks
            </li>
          </ul>
          <p>
            Failure to cooperate may affect eligibility for future services and may increase the
            likelihood of formal recovery action.
          </p>
        </div>

        {/* Section 11 */}
        <div className="doc-section">
          <span className="section-number">Section 11</span>
          <h2 className="section-title">Fraud, Abuse, and Deliberate Evasion</h2>
          <p>
            MegaTransact treats fraud, deception, document falsification, impersonation, account
            manipulation, and deliberate repayment evasion seriously.
          </p>
          <p>Where MegaTransact reasonably suspects misconduct, it may:</p>
          <ul>
            <li>suspend or terminate access</li>
            <li>preserve relevant records</li>
            <li>reject pending applications</li>
            <li>escalate the matter internally</li>
            <li>
              engage workplace-linked or third-party support where lawful
            </li>
            <li>
              report the matter to relevant authorities where required or justified by law
            </li>
          </ul>
        </div>

        {/* Section 12 */}
        <div className="doc-section">
          <span className="section-number">Section 12</span>
          <h2 className="section-title">No Waiver by Delay</h2>
          <p>
            If MegaTransact does not immediately enforce a right, issue a notice, or begin
            collections activity, this does not mean the obligation has been cancelled or waived.
          </p>
          <p>
            Any waiver, settlement, variation, or closure must be expressly confirmed by
            MegaTransact through an authorized process.
          </p>
        </div>

        {/* Section 13 */}
        <div className="doc-section">
          <span className="section-number">Section 13</span>
          <h2 className="section-title">Recordkeeping</h2>
          <p>MegaTransact may maintain records relating to:</p>
          <ul>
            <li>repayment schedules</li>
            <li>outstanding balances</li>
            <li>reminder history</li>
            <li>communications sent or received</li>
            <li>employer-linked follow-up where applicable</li>
            <li>recovery steps taken</li>
            <li>restrictions placed on the account</li>
            <li>dispute or resolution history</li>
          </ul>
          <p>
            These records may be used for operational, legal, audit, dispute-resolution,
            fraud-prevention, and compliance purposes, subject to applicable law.
          </p>
        </div>

        {/* Section 14 */}
        <div className="doc-section">
          <span className="section-number">Section 14</span>
          <h2 className="section-title">Privacy and Data Handling</h2>
          <p>
            Information used in responsible-use review and collections activity will be handled
            in line with the MegaTransact Privacy Policy.
          </p>
          <p>
            This may include use of personal, technical, employment-related, salary-linked, and
            repayment-related information where reasonably necessary for:
          </p>
          <ul>
            <li>service administration</li>
            <li>verification</li>
            <li>repayment management</li>
            <li>fraud prevention</li>
            <li>collections activity</li>
            <li>dispute handling</li>
            <li>compliance and security purposes</li>
          </ul>
        </div>

        {/* Section 15 */}
        <div className="doc-section">
          <span className="section-number">Section 15</span>
          <h2 className="section-title">Changes to This Notice</h2>
          <p>
            MegaTransact may update this Responsible Use and Collections Notice from time to time
            to reflect service refinement, operational improvements, legal requirements, or
            changes in recovery processes.
          </p>
          <p>
            When material updates are made, MegaTransact may notify users through the app,
            website, email, or other appropriate channels.
          </p>
        </div>

        {/* Section 16 */}
        <div className="doc-section">
          <span className="section-number">Section 16</span>
          <h2 className="section-title">Contact Us</h2>
          <p>
            If you have questions, requests, or concerns about this Responsible Use and
            Collections Notice, please contact:
          </p>
          <div className="doc-contact-box">
            <p>
              <strong>MegaTransact</strong>
            </p>
            <p>
              <strong>Email:</strong>{' '}
              <a href="mailto:admin@megablissworldwide.com">admin@megablissworldwide.com</a>
            </p>
            <p>
              <strong>Company:</strong> MegaBliss LTD
            </p>
            <p>
              <strong>Address:</strong> Bâtiment EIFUN, Avenue Kabalo n° 171, croisement Huilerie-Kabalo, Kinshasa, 4ᵉ niveau, bureau n° 45
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResponsibleUse;
